// ─── Customer Conflict View ──────────────────────────────────────────────────
//
// Blue/indigo accent to match ENTITY_TYPE_CONFIG.CUSTOMER.tone = 'info'.
// Traceability: FR-SYNC-013 (conflict resolution).

import { CUSTOMER_SCHEMA, formatPhone } from '@bbpos/utils';
import { GroupCard } from './GroupCard';
import { ConflictHeader } from './ConflictHeader';

export interface CustomerConflictViewProps {
  clientVersion: Record<string, unknown>;
  serverVersion: Record<string, unknown>;
  deviceId: string | null;
  conflictMessage?: string;
}

export function CustomerConflictView({
  clientVersion,
  serverVersion,
  deviceId,
  conflictMessage,
}: CustomerConflictViewProps) {
  const customerName =
    (clientVersion.name as string | undefined) ??
    (serverVersion.name as string | undefined) ??
    'Unknown Customer';

  const phone =
    (clientVersion.phone as string | undefined) ??
    (serverVersion.phone as string | undefined);

  const isActive =
    (clientVersion.is_active as boolean | undefined) ??
    (serverVersion.is_active as boolean | undefined);

  return (
    <div className="space-y-4 animate-fade-in" data-testid="customer-conflict-view">
      <ConflictHeader
        entityLabel="Customer Conflict"
        identifier={customerName}
        accent={{
          from: 'from-blue-500/10',
          to: 'to-indigo-500/10',
          ring: 'ring-blue-500/20',
          text: 'text-blue-700',
          darkText: 'dark:text-blue-300',
        }}
        tone="brand"
        badgeVariant="info"
        badgeLabel={isActive === false ? 'Inactive' : 'Active'}
        subtitle={phone ? (
          <p className="mt-0.5 text-sm font-mono tabular-nums text-neutral-600 dark:text-neutral-400">
            {formatPhone(phone)}
          </p>
        ) : undefined}
        deviceId={deviceId}
        conflictMessage={conflictMessage}
        ariaLabel="Customer conflict header"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {CUSTOMER_SCHEMA.groups.map((group, i) => (
          <GroupCard
            key={group.title}
            title={group.title}
            fields={group.fields}
            clientVersion={clientVersion}
            serverVersion={serverVersion}
            delay={i * 60}
          />
        ))}
      </div>
    </div>
  );
}
